import {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
} from "react";

import { OTP_LENGTH } from "../../authPolicy";

import "./styles.css";

const digitsOnly = (value) => String(value || "").replace(/\D/g, "");

/**
 * A code group rendered as {OTP_LENGTH} cells.
 *
 * Two behaviours are load-bearing and easy to lose:
 *
 *  - **Paste and autofill spread.** Any input event carrying more than one
 *    character is treated as a paste and the digits are spread across the
 *    remaining cells. Browsers routinely dump all six digits of an SMS
 *    autofill into whichever box has focus.
 *  - **`autocomplete="one-time-code"` sits on the FIRST cell only.** On the
 *    others it makes browsers offer the code again mid-entry.
 */
const OtpInput = forwardRef(function OtpInput(
  { value, onChange, disabled = false, autoFocus = false, label, id, invalid },
  ref
) {
  const cells = useRef([]);

  const chars = useMemo(() => {
    const filled = digitsOnly(value).slice(0, OTP_LENGTH).split("");
    return Array.from({ length: OTP_LENGTH }, (_, i) => filled[i] ?? "");
  }, [value]);

  const focusIndex = useCallback((index) => {
    const target = cells.current[Math.max(0, Math.min(index, OTP_LENGTH - 1))];
    target?.focus();
    target?.select?.();
  }, []);

  useImperativeHandle(ref, () => ({
    /**
     * Focus the first gap rather than blindly box 1, so an autofilled second
     * code followed by a manual first one lands where the user expects.
     */
    focusFirstEmpty: () => {
      const gap = chars.findIndex((char) => !char);
      focusIndex(gap === -1 ? OTP_LENGTH - 1 : gap);
    },
  }));

  const commit = (next) => onChange(next.slice(0, OTP_LENGTH));

  const handleChange = (index) => (event) => {
    const raw = event.target.value;
    const digits = digitsOnly(raw);

    // More than one character means a paste or an autofill dump.
    if (raw.length > 1 || digits.length > 1) {
      const next = (chars.join("").slice(0, index) + digits).slice(0, OTP_LENGTH);

      commit(next);
      focusIndex(next.length);
      return;
    }

    const next = [...chars];
    next[index] = digits.slice(-1);

    commit(next.join("").replace(/\s/g, ""));

    if (digits) focusIndex(index + 1);
  };

  const handleKeyDown = (index) => (event) => {
    if (event.key === "Backspace") {
      event.preventDefault();

      const next = [...chars];

      // Backspace on an empty cell steps back and clears the previous one.
      if (!next[index] && index > 0) {
        next[index - 1] = "";
        focusIndex(index - 1);
      } else {
        next[index] = "";
      }

      commit(next.join(""));
      return;
    }

    if (event.key === "ArrowLeft") {
      event.preventDefault();
      focusIndex(index - 1);
    }

    if (event.key === "ArrowRight") {
      event.preventDefault();
      focusIndex(index + 1);
    }
  };

  return (
    <div className="otp-group">
      {label && (
        <label className="otp-label" htmlFor={`${id}-0`}>
          {label}
        </label>
      )}

      <div className={`otp-cells${invalid ? " otp-cells--invalid" : ""}`}>
        {chars.map((char, index) => (
          <input
            key={index}
            id={`${id}-${index}`}
            ref={(node) => {
              cells.current[index] = node;
            }}
            className="otp-cell"
            value={char}
            onChange={handleChange(index)}
            onKeyDown={handleKeyDown(index)}
            onFocus={(event) => event.target.select()}
            disabled={disabled}
            autoFocus={autoFocus && index === 0}
            inputMode="numeric"
            autoComplete={index === 0 ? "one-time-code" : "off"}
            maxLength={OTP_LENGTH}
            aria-label={`${label || "Verification code"} digit ${index + 1}`}
          />
        ))}
      </div>
    </div>
  );
});

export default OtpInput;
